// src/pages/ZohoTab.js
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Button,
  Snackbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CableIcon from "@mui/icons-material/Cable";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SettingsIcon from "@mui/icons-material/Settings";
import axios from "axios";
import { useAuth } from "../../hooks/AuthProvider";
import {
  ZOHO_GET_CREDENTIALS_ENDPOINT,
  ZOHO_SYNC_LEADS_ENDPOINT,
  ZOHO_STATUS_ENDPOINT,
  ZOHO_DISCONNECT_ENDPOINT,
  ZOHO_CONFIGURATION_ENDPOINT,
  ZOHO_SYNC_PROGRESS_ENDPOINT,
} from "../../config/urls";
import {
  ColorsConstants,
  borderColors,
  grayFontColor,
  dashboardCardGreenTextColor,
} from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";

// ── Zoho module / BotBridge table definitions ───────────────────────────────
// BB_Leads / BB_Customer field lists mirror the real lead_generation.Lead and
// lead_generation.Customer columns — see those models for the source of truth.

const ZOHO_MODULES = {
  Leads: {
    icon: TrackChangesIcon,
    description: "Unqualified prospects not yet converted to contacts",
    fields: [
      { id: "First_Name", label: "First Name", type: "Text", required: false },
      { id: "Last_Name", label: "Last Name", type: "Text", required: true },
      { id: "Company", label: "Company", type: "Text", required: true },
      { id: "Email", label: "Email", type: "Email", required: false },
      { id: "Phone", label: "Phone", type: "Phone", required: false },
      { id: "Mobile", label: "Mobile", type: "Phone", required: false },
      { id: "Lead_Source", label: "Lead Source", type: "Picklist", required: false },
      { id: "Lead_Status", label: "Lead Status", type: "Picklist", required: false },
      { id: "Industry", label: "Industry", type: "Picklist", required: false },
      { id: "Annual_Revenue", label: "Annual Revenue", type: "Currency", required: false },
      { id: "City", label: "City", type: "Text", required: false },
      { id: "State", label: "State", type: "Text", required: false },
      { id: "Description", label: "Description", type: "Text Area", required: false },
    ],
  },
  Contacts: {
    icon: PeopleIcon,
    description: "Converted leads and known individuals linked to accounts",
    fields: [
      { id: "First_Name", label: "First Name", type: "Text", required: false },
      { id: "Last_Name", label: "Last Name", type: "Text", required: true },
      { id: "Account_Name", label: "Account Name", type: "Lookup", required: false },
      { id: "Email", label: "Email", type: "Email", required: false },
      { id: "Phone", label: "Phone", type: "Phone", required: false },
      { id: "Mobile", label: "Mobile", type: "Phone", required: false },
      { id: "Lead_Source", label: "Lead Source", type: "Picklist", required: false },
      { id: "Title", label: "Title", type: "Text", required: false },
      { id: "Department", label: "Department", type: "Text", required: false },
      { id: "Mailing_City", label: "Mailing City", type: "Text", required: false },
      { id: "Mailing_State", label: "Mailing State", type: "Text", required: false },
      { id: "Owner", label: "Owner", type: "Lookup", required: false },
      { id: "Description", label: "Description", type: "Text Area", required: false },
    ],
  },
  Accounts: {
    icon: BusinessIcon,
    description: "Companies and organizations tied to contacts and deals",
    fields: [
      { id: "Account_Name", label: "Account Name", type: "Text", required: true },
      { id: "Website", label: "Website", type: "URL", required: false },
      { id: "Phone", label: "Phone", type: "Phone", required: false },
      { id: "Industry", label: "Industry", type: "Picklist", required: false },
      { id: "Annual_Revenue", label: "Annual Revenue", type: "Currency", required: false },
      { id: "Employees", label: "Employees", type: "Number", required: false },
      { id: "Billing_City", label: "Billing City", type: "Text", required: false },
      { id: "Billing_State", label: "Billing State", type: "Text", required: false },
      { id: "Owner", label: "Owner", type: "Lookup", required: false },
      { id: "Description", label: "Description", type: "Text Area", required: false },
    ],
  },
};

// Zoho Leads AND Zoho Contacts both feed the BB "Leads" table; Zoho Accounts feeds "Customers".
const MODULE_TO_BB_TABLE = {
  Leads: "BB_Leads",
  Contacts: "BB_Leads",
  Accounts: "BB_Customer",
};

const BB_TABLES = {
  BB_Leads: {
    label: "Leads",
    description: "Fed by Zoho Leads and Zoho Contacts",
    fields: [
      { value: "", label: "Don't import" },
      { value: "first_name", label: "First Name" },
      { value: "last_name", label: "Last Name" },
      { value: "phone", label: "Phone (WhatsApp number)" },
      { value: "email", label: "Email" },
      { value: "company", label: "Company" },
      { value: "city", label: "City" },
      { value: "state", label: "State" },
      { value: "source", label: "Source" },
      { value: "status", label: "Status" },
      { value: "owner", label: "Owner" },
      { value: "tags", label: "Tags" },
      { value: "description", label: "Description" },
      { value: "enquiry_type", label: "Enquiry Type" },
      { value: "custom_1", label: "Custom Field 1" },
      { value: "custom_2", label: "Custom Field 2" },
      { value: "custom_3", label: "Custom Field 3" },
      { value: "__new__", label: "+ Create new custom field" },
    ],
  },
  BB_Customer: {
    label: "Customers",
    description: "Fed by Zoho Accounts",
    fields: [
      { value: "", label: "Don't import" },
      { value: "customer_name", label: "Customer Name" },
      { value: "website", label: "Website" },
      { value: "phone", label: "Phone (WhatsApp number)" },
      { value: "industry", label: "Industry" },
      { value: "annual_revenue", label: "Annual Revenue" },
      { value: "employees", label: "Employees" },
      { value: "city", label: "City" },
      { value: "state", label: "State" },
      { value: "owner", label: "Owner" },
      { value: "description", label: "Description" },
      { value: "custom_1", label: "Custom Field 1" },
      { value: "custom_2", label: "Custom Field 2" },
      { value: "custom_3", label: "Custom Field 3" },
      { value: "__new__", label: "+ Create new custom field" },
    ],
  },
};

const AUTO_MAP = {
  Leads: {
    First_Name: "first_name",
    Last_Name: "last_name",
    Company: "company",
    Email: "email",
    Phone: "phone",
    Mobile: "phone",
    Lead_Source: "source",
    Lead_Status: "status",
    Industry: "custom_1",
    Annual_Revenue: "custom_2",
    City: "city",
    State: "state",
    Description: "description",
  },
  Contacts: {
    First_Name: "first_name",
    Last_Name: "last_name",
    Account_Name: "company",
    Email: "email",
    Phone: "phone",
    Mobile: "phone",
    Lead_Source: "source",
    Title: "custom_1",
    Department: "custom_2",
    Mailing_City: "city",
    Mailing_State: "state",
    Owner: "owner",
    Description: "description",
  },
  Accounts: {
    Account_Name: "customer_name",
    Website: "website",
    Phone: "phone",
    Industry: "industry",
    Annual_Revenue: "annual_revenue",
    Employees: "employees",
    Billing_City: "city",
    Billing_State: "state",
    Owner: "owner",
    Description: "description",
  },
};

const FREQUENCY_OPTIONS = ["Daily (once)"];
const FIXED_SYNC_TIME = "2:00 AM IST";
const DEFAULT_MODULES = ["Leads", "Contacts"];

const todayISO = () => new Date().toISOString().slice(0, 10);

const buildAutoMapping = (selectedModules) => {
  const mapping = {};
  selectedModules.forEach((mod) => {
    mapping[mod] = {};
    ZOHO_MODULES[mod].fields.forEach((field) => {
      const guess = AUTO_MAP[mod]?.[field.id];
      mapping[mod][field.id] = guess !== undefined ? guess : "";
    });
  });
  return mapping;
};

const ALL_STEPS = [
  { key: "connect", label: "Connect" },
  { key: "modules", label: "Select modules" },
  { key: "mapping", label: "Choose & map fields" },
  { key: "schedule", label: "Schedule sync" },
];

const defaultSchedule = () => ({
  frequency: "Daily (once)",
  startDate: todayISO(),
  noEndDate: true,
  endDate: "",
});

const STATUS_LABELS = {
  PENDING: "in progress",
  COMPLETED: "processing results",
  DOWNLOADED: "done",
  FAILED: "failed",
  NOT_STARTED: "not started",
};

const timeAgo = (dateInput) => {
  if (!dateInput) return null;
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return null;

  const seconds = Math.max(1, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const ZohoTab = () => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  // Real OAuth credential fetch — unchanged from the previous single-page version.
  const [loadingCredentials, setLoadingCredentials] = useState(true);
  const [zohoUrl, setZohoUrl] = useState("");

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  // Wizard state — modules / mapping / schedule have no backend yet, so they're
  // local UI state only and reset to defaults whenever a real connection is
  // (re)established. Only `hasIntegration` itself is backed by the ZohoAuth row.
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [hasIntegration, setHasIntegration] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [step, setStep] = useState("connect"); // connect | modules | mapping | schedule | syncing | status
  const [selectedModules, setSelectedModules] = useState(DEFAULT_MODULES);
  const [fieldMappings, setFieldMappings] = useState(() => buildAutoMapping(DEFAULT_MODULES));
  const [expandedMapModule, setExpandedMapModule] = useState(DEFAULT_MODULES[0]);
  const [schedule, setSchedule] = useState(defaultSchedule());
  const [syncMessage, setSyncMessage] = useState(null);
  const [moduleProgress, setModuleProgress] = useState(null);
  const [lastSyncTimestamp, setLastSyncTimestamp] = useState(null);
  const [anySyncFailed, setAnySyncFailed] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      if (!tenant) {
        setCheckingStatus(false);
        return;
      }

      try {
        const token = await getValidToken();
        const { data } = await axios.get(ZOHO_STATUS_ENDPOINT, {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-ID": tenant,
          },
        });

        if (data.connected) {
          setHasIntegration(true);
          setStep("status");

          try {
            const { data: config } = await axios.get(ZOHO_CONFIGURATION_ENDPOINT, {
              headers: {
                Authorization: `Bearer ${token}`,
                "X-Tenant-ID": tenant,
              },
            });

            if (config.selected_modules && config.selected_modules.length > 0) {
              setSelectedModules(config.selected_modules);
              setFieldMappings(buildAutoMapping(config.selected_modules));
              setExpandedMapModule(config.selected_modules[0]);
            }
            setSchedule({
              frequency: config.frequency || "Daily (Once)",
              startDate: config.start_date || todayISO(),
              noEndDate: !config.end_date,
              endDate: config.end_date || "",
            });
          } catch (configError) {
            console.error("Failed to fetch Zoho configuration", configError);
          }
        }
      } catch (error) {
        console.error("Failed to fetch Zoho status", error);
      } finally {
        setCheckingStatus(false);
      }
    };

    fetchStatus();
  }, [getValidToken, tenant]);

  const fetchProgress = useCallback(async () => {
    if (!tenant) return null;
    try {
      const token = await getValidToken();
      const { data } = await axios.get(ZOHO_SYNC_PROGRESS_ENDPOINT, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": tenant,
        },
      });

      setModuleProgress(data.modules || {});
      setAnySyncFailed(Boolean(data.any_failed));
      setLastSyncTimestamp(data.last_sync_timestamp || null);
      if (!data.modules || Object.keys(data.modules).length === 0) {
        setSyncMessage("No sync has run yet.");
      } else {
        setSyncMessage(null);
      }
      return data;
    } catch (error) {
      console.error("Failed to fetch Zoho sync progress", error);
      return null;
    }
  }, [getValidToken, tenant]);

  // Polls bulk-import progress while the Status screen is showing a connected
  // tenant, so real completion (per-module created/updated/skipped/error counts)
  // replaces the optimistic "import started" message — and survives a page
  // reload, since moduleProgress/lastSyncTimestamp are otherwise sourced from
  // the server on each fetch rather than persisted client state.
  useEffect(() => {
    if (!hasIntegration || step !== "status") return undefined;

    fetchProgress();
    const intervalId = setInterval(async () => {
      const data = await fetchProgress();
      if (data && !data.in_progress) {
        clearInterval(intervalId);
      }
    }, 15000);

    return () => clearInterval(intervalId);
  }, [hasIntegration, step, fetchProgress]);

  useEffect(() => {
    const fetchCredentials = async () => {
      if (!tenant) {
        showSnackbar("Tenant not found in local storage.", "error");
        setLoadingCredentials(false);
        return;
      }

      try {
        const token = await getValidToken();
        const { data } = await axios.get(ZOHO_GET_CREDENTIALS_ENDPOINT, {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-ID": tenant,
          },
        });

        let { zoho_client_id, zoho_scope, zoho_redirect_uri } = data;
        const redirectUri = zoho_redirect_uri
          ? zoho_redirect_uri
          : window.location.hostname === "localhost"
          ? "http://localhost:3000/callback"
          : `${window.location.origin}/callback/`;

        const url = `https://accounts.zoho.in/oauth/v2/auth?scope=${encodeURIComponent(
          zoho_scope
        )}&client_id=${zoho_client_id}&response_type=code&access_type=offline&redirect_uri=${encodeURIComponent(
          redirectUri
        )}`;

        setZohoUrl(url);
      } catch (error) {
        console.error("Failed to fetch Zoho credentials", error);
        showSnackbar("Failed to load Zoho credentials.", "error");
      } finally {
        setLoadingCredentials(false);
      }
    };

    fetchCredentials();
  }, [getValidToken, tenant]);

  const handleConnect = () => {
    if (zohoUrl) {
      window.location.href = zohoUrl;
    } else {
      showSnackbar("Zoho URL not ready yet. Please try again.", "error");
    }
  };

  const toggleModule = (mod) => {
    setSelectedModules((prev) => (prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod]));
  };

  const goToMapping = () => {
    setFieldMappings((prev) => {
      const fresh = buildAutoMapping(selectedModules);
      // Preserve any edits already made for modules the user had visited before.
      selectedModules.forEach((mod) => {
        if (prev[mod]) fresh[mod] = { ...fresh[mod], ...prev[mod] };
      });
      return fresh;
    });
    setExpandedMapModule(selectedModules[0] || null);
    setStep("mapping");
  };

  const mappedCount = (mod) =>
    Object.values(fieldMappings[mod] || {}).filter((v) => v && v !== "").length;
  const totalMappedCount = selectedModules.reduce((sum, m) => sum + mappedCount(m), 0);

  const updateSchedule = (patch) => setSchedule((prev) => ({ ...prev, ...patch }));

  const handleSaveSchedule = async () => {
    if (!tenant) {
      showSnackbar("Tenant not found.", "error");
      return;
    }
    setStep("syncing");
    setSyncing(true);
    try {
      const token = await getValidToken();
      const { data } = await axios.post(
        ZOHO_SYNC_LEADS_ENDPOINT,
        {
          selected_modules: selectedModules,
          frequency: schedule.frequency,
          start_date: schedule.startDate || null,
          end_date: schedule.noEndDate ? null : schedule.endDate || null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-ID": tenant,
          },
        }
      );
      // Real progress (per-module record counts / failures) arrives via the
      // polling effect once step flips to "status" — this is just an optimistic
      // placeholder for the brief moment before the first progress fetch resolves.
      setSyncMessage(data.message || "Import started…");
      setHasIntegration(true);
      setStep("status");
    } catch (error) {
      console.error("Sync failed", error);
      showSnackbar("Failed to sync leads.", "error");
      setStep("schedule");
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!tenant) {
      showSnackbar("Tenant not found.", "error");
      return;
    }
    setDisconnecting(true);
    try {
      const token = await getValidToken();
      await axios.post(
        ZOHO_DISCONNECT_ENDPOINT,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-ID": tenant,
          },
        }
      );
      setHasIntegration(false);
      setSyncMessage(null);
      setModuleProgress(null);
      setLastSyncTimestamp(null);
      setAnySyncFailed(false);
      setSelectedModules(DEFAULT_MODULES);
      setFieldMappings(buildAutoMapping(DEFAULT_MODULES));
      setExpandedMapModule(DEFAULT_MODULES[0]);
      setSchedule(defaultSchedule());
      setStep("connect");
      showSnackbar("Zoho CRM disconnected.", "success");
    } catch (error) {
      console.error("Failed to disconnect Zoho", error);
      showSnackbar("Failed to disconnect Zoho.", "error");
    } finally {
      setDisconnecting(false);
    }
  };

  const goBack = () => {
    if (step === "modules") setStep(hasIntegration ? "status" : "connect");
    else if (step === "mapping") setStep("modules");
    else if (step === "schedule") setStep("mapping");
  };

  const goForward = () => {
    if (step === "modules") goToMapping();
    else if (step === "mapping") setStep("schedule");
    else if (step === "schedule") handleSaveSchedule();
  };

  const wizardSteps = hasIntegration ? ALL_STEPS.slice(1) : ALL_STEPS;
  const effectiveStepKey = step === "syncing" ? "schedule" : step;
  const activeStepIndex = wizardSteps.findIndex((s) => s.key === effectiveStepKey);
  const isWizardStep = ["connect", "modules", "mapping", "schedule"].includes(step);
  const showFooterNav = ["modules", "mapping", "schedule"].includes(step);

  return (
    <Box sx={{ bgcolor: "#F5F7FB", minHeight: "80vh", p: 4, display: "flex", justifyContent: "center" }}>
      <Snackbar
        open={snackbar.open}
        sx={{ mb: 10 }}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ width: "100%", maxWidth: 720 }}>
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: 3,
            border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
            p: 3.5,
            boxShadow: "0 1px 3px rgba(8,36,92,0.06)",
          }}
        >
          {/* ── Header ── */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Box
              sx={{
                width: 40, height: 40, borderRadius: "10px",
                bgcolor: hasIntegration ? "#E7F6EC" : ColorsConstants.primaryPurple60,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <CableIcon sx={{ fontSize: 20, color: hasIntegration ? dashboardCardGreenTextColor : ColorsConstants.secondaryBackgroundColor }} />
            </Box>
            <Box>
              <Typography sx={{ ...typography.cardTitle, color: ColorsConstants.primaryBackgroundColor }}>
                Zoho CRM Integration
              </Typography>
              <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.25 }}>
                {checkingStatus ? (
                  "Checking connection status…"
                ) : hasIntegration ? (
                  <Box component="span" sx={{ color: dashboardCardGreenTextColor, fontWeight: 700 }}>
                    ● Connected to Zoho CRM
                  </Box>
                ) : (
                  "Not connected"
                )}
              </Typography>
            </Box>
          </Box>

          {checkingStatus && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {/* ── Stepper ── */}
          {!checkingStatus && isWizardStep && activeStepIndex !== -1 && (
            <Stepper activeStep={activeStepIndex} sx={{ mt: 3, mb: 1 }}>
              {wizardSteps.map((s) => (
                <Step key={s.key}>
                  <StepLabel>{s.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {/* ── STEP: CONNECT ── */}
          {!checkingStatus && step === "connect" && (
            <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
              <Box
                sx={{
                  width: 56, height: 56, borderRadius: "50%", bgcolor: ColorsConstants.primaryPurple60,
                  display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2.25,
                }}
              >
                <CableIcon sx={{ fontSize: 26, color: ColorsConstants.secondaryBackgroundColor }} />
              </Box>
              <Typography sx={{ ...typography.cardTitle, color: ColorsConstants.primaryBackgroundColor, mb: 1 }}>
                Connect your Zoho CRM account
              </Typography>
              <Typography sx={{ ...typography.bodyLarge, color: grayFontColor, maxWidth: 420, mx: "auto", mb: 3, lineHeight: 1.6 }}>
                Authorize BotBridge to read Leads, Contacts, and Accounts data. You'll be redirected to
                Zoho to grant access, then brought back here.
              </Typography>

              {loadingCredentials ? (
                <CircularProgress />
              ) : (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  size="large"
                  onClick={handleConnect}
                  sx={{
                    bgcolor: "#ff5e00",
                    "&:hover": { bgcolor: "#e05600" },
                    textTransform: "none",
                    px: 4, py: 1.5, borderRadius: 2, fontWeight: "bold", fontSize: "1rem",
                  }}
                >
                  Connect with Zoho
                </Button>
              )}

              <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray30, mt: 1.75 }}>
                Uses OAuth 2.0 — BotBridge never sees your Zoho password
              </Typography>
            </Box>
          )}

          {/* ── STEP: MODULES ── */}
          {!checkingStatus && step === "modules" && (
            <Box sx={{ py: 1 }}>
              <Typography sx={{ ...typography.bodyLarge, color: grayFontColor, mb: 2 }}>
                Choose which Zoho CRM modules to sync with BotBridge.
              </Typography>
              <Stack spacing={1.25}>
                {Object.entries(ZOHO_MODULES).map(([mod, data]) => {
                  const Icon = data.icon;
                  const isSelected = selectedModules.includes(mod);
                  return (
                    <Box
                      key={mod}
                      onClick={() => toggleModule(mod)}
                      sx={{
                        display: "flex", alignItems: "center", gap: 1.75, p: 2,
                        border: `1.5px solid ${isSelected ? ColorsConstants.secondaryBackgroundColor : borderColors.cardBorderPrimaryGrayColor}`,
                        bgcolor: isSelected ? ColorsConstants.primaryPurple60 : "#fff",
                        borderRadius: 2.5, cursor: "pointer", transition: "all 0.15s",
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleModule(mod)}
                        sx={{ p: 0 }}
                      />
                      <Box
                        sx={{
                          width: 36, height: 36, borderRadius: "8px", bgcolor: ColorsConstants.primaryPurple60,
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 18, color: ColorsConstants.secondaryBackgroundColor }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ ...typography.cardTitle, fontSize: 14, color: ColorsConstants.primaryBackgroundColor }}>
                          {mod}
                        </Typography>
                        <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.25 }}>
                          {data.description}
                        </Typography>
                      </Box>
                      <Typography sx={{ ...typography.bodySmall, color: grayFontColor, whiteSpace: "nowrap" }}>
                        {data.fields.length} fields available
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* ── STEP: FIELD MAPPING (view-only) ── */}
          {!checkingStatus && step === "mapping" && (
            <Box sx={{ py: 1 }}>
              <Typography sx={{ ...typography.bodyLarge, color: grayFontColor, mb: 2 }}>
                This is how each Zoho field will map to your BotBridge columns. This mapping is
                pre-configured and can't be edited here — review it, then continue.
              </Typography>
              <Stack spacing={2}>
                {Object.entries(BB_TABLES)
                  .filter(([bbKey]) => selectedModules.some((m) => MODULE_TO_BB_TABLE[m] === bbKey))
                  .map(([bbKey, bbTable]) => {
                    const sourceModules = selectedModules.filter((m) => MODULE_TO_BB_TABLE[m] === bbKey);
                    return (
                      <Box
                        key={bbKey}
                        sx={{ border: `1.5px solid ${ColorsConstants.secondaryBackgroundColor}33`, borderRadius: 2.5, overflow: "hidden" }}
                      >
                        <Box sx={{ bgcolor: ColorsConstants.primaryPurple60, px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
                          <Chip
                            label="BB TABLE"
                            size="small"
                            sx={{ bgcolor: ColorsConstants.primaryBackgroundColor, color: "#fff", fontWeight: 700, fontSize: 11, height: 20 }}
                          />
                          <Typography sx={{ ...typography.cardTitle, fontSize: 14.5, color: ColorsConstants.primaryBackgroundColor }}>
                            {bbTable.label}
                          </Typography>
                          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>— {bbTable.description}</Typography>
                        </Box>

                        <Box sx={{ px: 2, pb: 1.75, pt: 0.5 }}>
                          {sourceModules.map((mod) => {
                            const ModIcon = ZOHO_MODULES[mod].icon;
                            const allFields = ZOHO_MODULES[mod].fields;
                            const mapped = mappedCount(mod);
                            return (
                              <Accordion
                                key={mod}
                                expanded={expandedMapModule === mod}
                                onChange={() => setExpandedMapModule(expandedMapModule === mod ? null : mod)}
                                disableGutters
                                sx={{
                                  mt: 1.5, border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                                  borderRadius: "10px !important", "&:before": { display: "none" }, boxShadow: "none",
                                }}
                              >
                                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: grayFontColor }} />}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, width: "100%" }}>
                                    <ModIcon sx={{ fontSize: 16, color: ColorsConstants.secondaryBackgroundColor }} />
                                    <Typography sx={{ ...typography.cardTitle, fontSize: 13.5, color: ColorsConstants.primaryBackgroundColor, flex: 1 }}>
                                      Zoho {mod}
                                    </Typography>
                                    <Chip
                                      label={`${mapped} of ${allFields.length} fields mapped`}
                                      size="small"
                                      sx={{ bgcolor: ColorsConstants.primaryPurple60, color: ColorsConstants.secondaryBackgroundColor, fontWeight: 600, fontSize: 11 }}
                                    />
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 24px 1.1fr", gap: 1.25, alignItems: "center", mb: 1 }}>
                                    <Typography sx={{ fontSize: 11, color: ColorsConstants.primaryGray30, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>
                                      Zoho field
                                    </Typography>
                                    <span />
                                    <Typography sx={{ fontSize: 11, color: ColorsConstants.primaryGray30, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>
                                      BB: {bbTable.label} column
                                    </Typography>
                                  </Box>
                                  {allFields.map((field) => {
                                    const mappedValue = (fieldMappings[mod] || {})[field.id] ?? "";
                                    const isUnmapped = mappedValue === "";
                                    return (
                                      <Box
                                        key={field.id}
                                        sx={{
                                          display: "grid", gridTemplateColumns: "1fr 24px 1.1fr", gap: 1.25,
                                          alignItems: "center", py: 1, borderTop: "1px solid #F0F2F7",
                                        }}
                                      >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                          <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryBackgroundColor }}>
                                            {field.label}
                                          </Typography>
                                          <Chip
                                            label={field.type}
                                            size="small"
                                            sx={{ fontSize: 10, height: 18, bgcolor: "#F0F2F7", color: ColorsConstants.primaryGray30 }}
                                          />
                                          {field.required && (
                                            <Typography sx={{ fontSize: 10, color: "#F26522", fontWeight: 700 }}>req</Typography>
                                          )}
                                        </Box>
                                        <ArrowForwardIcon sx={{ fontSize: 13, color: ColorsConstants.primaryGray20 }} />
                                        <Box
                                          sx={{
                                            fontSize: 13,
                                            fontWeight: isUnmapped ? 400 : 600,
                                            color: isUnmapped ? ColorsConstants.primaryGray30 : ColorsConstants.primaryBackgroundColor,
                                            border: `1.5px solid ${isUnmapped ? "#F0D9A8" : "#DCE2ED"}`,
                                            bgcolor: isUnmapped ? "#FFFBF2" : "#F8FAFD",
                                            borderRadius: "7px",
                                            px: 1.25, py: 0.75,
                                          }}
                                        >
                                          {bbTable.fields.find((opt) => opt.value === mappedValue)?.label || "Don't import"}
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </AccordionDetails>
                              </Accordion>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
              </Stack>
            </Box>
          )}

          {/* ── STEP: SCHEDULE ── */}
          {!checkingStatus && step === "schedule" && (
            <Box sx={{ py: 1 }}>
              <Typography sx={{ ...typography.bodyLarge, color: grayFontColor, mb: 2.5 }}>
                Set how often BotBridge should pull new or updated records from Zoho, and the date
                range to sync.
              </Typography>

              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ display: "flex", alignItems: "center", gap: 1, ...typography.bodySmall, fontWeight: 700, color: ColorsConstants.primaryBackgroundColor, mb: 1 }}>
                  <AccessTimeIcon sx={{ fontSize: 16, color: ColorsConstants.secondaryBackgroundColor }} /> Sync Frequency
                </Typography>
                <FormControl fullWidth size="small">
                  <Select value={schedule.frequency} onChange={(e) => updateSchedule({ frequency: e.target.value })}>
                    {FREQUENCY_OPTIONS.map((f) => (
                      <MenuItem key={f} value={f}>{f}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ display: "flex", alignItems: "center", gap: 1, ...typography.bodySmall, fontWeight: 700, color: ColorsConstants.primaryBackgroundColor, mb: 1 }}>
                  <AccessTimeIcon sx={{ fontSize: 16, color: ColorsConstants.secondaryBackgroundColor }} /> Sync Time
                </Typography>
                <Box
                  sx={{
                    ...typography.bodyLarge, color: ColorsConstants.primaryBackgroundColor,
                    border: `1.5px solid ${borderColors.cardBorderPrimaryGrayColor}`, bgcolor: "#F5F7FB",
                    borderRadius: 2, px: 1.5, py: 1.25,
                  }}
                >
                  {FIXED_SYNC_TIME}
                </Box>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
                <Box>
                  <Typography sx={{ display: "flex", alignItems: "center", gap: 1, ...typography.bodySmall, fontWeight: 700, color: ColorsConstants.primaryBackgroundColor, mb: 1 }}>
                    <CalendarMonthIcon sx={{ fontSize: 16, color: ColorsConstants.secondaryBackgroundColor }} /> Start Date
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={schedule.startDate}
                    onChange={(e) => updateSchedule({ startDate: e.target.value })}
                  />
                </Box>
                <Box>
                  <Typography sx={{ display: "flex", alignItems: "center", gap: 1, ...typography.bodySmall, fontWeight: 700, color: ColorsConstants.primaryBackgroundColor, mb: 1 }}>
                    <CalendarMonthIcon sx={{ fontSize: 16, color: ColorsConstants.secondaryBackgroundColor }} /> End Date
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={schedule.endDate}
                    disabled={schedule.noEndDate}
                    inputProps={{ min: schedule.startDate }}
                    onChange={(e) => updateSchedule({ endDate: e.target.value })}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Checkbox
                  checked={schedule.noEndDate}
                  onChange={(e) => updateSchedule({ noEndDate: e.target.checked, endDate: e.target.checked ? "" : schedule.endDate })}
                  sx={{ p: 0 }}
                />
                <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                  No end date — keep syncing indefinitely
                </Typography>
              </Box>

              <Alert
                severity="info"
                icon={<RefreshIcon sx={{ fontSize: 16 }} />}
                sx={{ bgcolor: ColorsConstants.primaryPurple60, color: ColorsConstants.primaryBackgroundColor, ...typography.bodySmall }}
              >
                Syncing <strong>{totalMappedCount} fields</strong> across <strong>{selectedModules.length} modules</strong>,
                {" "}starting <strong>{schedule.startDate || "today"}</strong>, <strong>{schedule.frequency.toLowerCase()}</strong> at{" "}
                <strong>{FIXED_SYNC_TIME}</strong>
                {schedule.noEndDate ? ", with no end date." : ` until ${schedule.endDate || "—"}.`}
              </Alert>
            </Box>
          )}

          {/* ── STEP: SYNCING (real API call) ── */}
          {!checkingStatus && step === "syncing" && (
            <Box sx={{ textAlign: "center", py: 5.5 }}>
              <Box
                sx={{
                  width: 52, height: 52, borderRadius: "50%", bgcolor: ColorsConstants.primaryPurple60,
                  display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2.25,
                }}
              >
                <CircularProgress size={24} sx={{ color: ColorsConstants.secondaryBackgroundColor }} />
              </Box>
              <Typography sx={{ ...typography.cardTitle, fontSize: 15, color: ColorsConstants.primaryBackgroundColor, mb: 0.5 }}>
                Syncing with Zoho CRM…
              </Typography>
              <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>
                Pulling {totalMappedCount} fields across {selectedModules.length} modules. This won't take long.
              </Typography>
            </Box>
          )}

          {/* ── STEP: STATUS (persistent view once connected) ── */}
          {!checkingStatus && step === "status" && (
            <Box sx={{ py: 0.5 }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
                <Button
                  size="small"
                  startIcon={<SettingsIcon sx={{ fontSize: 15 }} />}
                  onClick={() => setStep("modules")}
                  sx={{ textTransform: "none", ...typography.bodySmall, fontWeight: 600, color: ColorsConstants.secondaryBackgroundColor }}
                >
                  Edit sync settings
                </Button>
              </Box>

              <Box sx={{ border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: 2.5, p: 2, mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 16, color: ColorsConstants.secondaryBackgroundColor }} />
                  <Typography sx={{ ...typography.cardTitle, fontSize: 14, color: ColorsConstants.primaryBackgroundColor }}>
                    Sync Schedule
                  </Typography>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.25 }}>
                  <Box>
                    <Typography sx={{ fontSize: 10.5, color: ColorsConstants.primaryGray30, textTransform: "uppercase", fontWeight: 700 }}>Frequency</Typography>
                    <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryBackgroundColor, mt: 0.25 }}>{schedule.frequency}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10.5, color: ColorsConstants.primaryGray30, textTransform: "uppercase", fontWeight: 700 }}>Sync Time</Typography>
                    <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryBackgroundColor, mt: 0.25 }}>{FIXED_SYNC_TIME}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10.5, color: ColorsConstants.primaryGray30, textTransform: "uppercase", fontWeight: 700 }}>Start Date</Typography>
                    <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryBackgroundColor, mt: 0.25 }}>{schedule.startDate || "—"}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10.5, color: ColorsConstants.primaryGray30, textTransform: "uppercase", fontWeight: 700 }}>End Date</Typography>
                    <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryBackgroundColor, mt: 0.25 }}>
                      {schedule.noEndDate ? "No end date" : (schedule.endDate || "—")}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: 2.5, p: 2, mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <SettingsIcon sx={{ fontSize: 16, color: ColorsConstants.secondaryBackgroundColor }} />
                  <Typography sx={{ ...typography.cardTitle, fontSize: 14, color: ColorsConstants.primaryBackgroundColor }}>
                    Modules & Field Mapping
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  {selectedModules.map((mod) => {
                    const ModIcon = ZOHO_MODULES[mod].icon;
                    const bbTable = BB_TABLES[MODULE_TO_BB_TABLE[mod]];
                    return (
                      <Box key={mod} sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
                        <ModIcon sx={{ fontSize: 14, color: ColorsConstants.secondaryBackgroundColor }} />
                        <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryBackgroundColor, fontWeight: 600, flex: 1 }}>
                          Zoho {mod}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: grayFontColor }}>→ BB: {bbTable.label}</Typography>
                        <Chip
                          label={`${mappedCount(mod)} fields`}
                          size="small"
                          sx={{ fontSize: 11, height: 20, bgcolor: ColorsConstants.primaryPurple60, color: ColorsConstants.secondaryBackgroundColor }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              </Box>

              <Box sx={{ border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: 2.5, p: 2, mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                  <Typography sx={{ ...typography.cardTitle, fontSize: 14, color: ColorsConstants.primaryBackgroundColor }}>
                    Last Sync Result
                  </Typography>
                  {timeAgo(lastSyncTimestamp) && (
                    <Typography sx={{ fontSize: 11.5, color: grayFontColor }}>
                      {timeAgo(lastSyncTimestamp)}
                    </Typography>
                  )}
                </Box>

                {!moduleProgress || Object.keys(moduleProgress).length === 0 ? (
                  <Typography sx={{ ...typography.bodyLarge, color: ColorsConstants.primaryBackgroundColor }}>
                    {syncMessage || "No sync has run yet."}
                  </Typography>
                ) : (
                  <Stack spacing={0}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1.2fr 0.8fr 0.8fr 0.8fr 0.8fr 1fr",
                        gap: 1,
                        pb: 0.75,
                      }}
                    >
                      {["Module", "Created", "Updated", "Skipped", "Errors", "Status"].map((h) => (
                        <Typography
                          key={h}
                          sx={{ fontSize: 10.5, color: ColorsConstants.primaryGray30, textTransform: "uppercase", fontWeight: 700 }}
                        >
                          {h}
                        </Typography>
                      ))}
                    </Box>
                    {Object.entries(moduleProgress).map(([mod, info]) => {
                      const isTerminal = info.status === "DOWNLOADED" || info.status === "FAILED";
                      return (
                        <Box
                          key={mod}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1.2fr 0.8fr 0.8fr 0.8fr 0.8fr 1fr",
                            gap: 1,
                            alignItems: "center",
                            py: 1,
                            borderTop: "1px solid #F0F2F7",
                          }}
                        >
                          <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryBackgroundColor, fontWeight: 600 }}>
                            {mod}
                          </Typography>
                          <Typography sx={{ ...typography.bodySmall, color: dashboardCardGreenTextColor }}>
                            {isTerminal ? info.created_count ?? 0 : "—"}
                          </Typography>
                          <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.secondaryBackgroundColor }}>
                            {isTerminal ? info.updated_count ?? 0 : "—"}
                          </Typography>
                          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                            {isTerminal ? info.skipped_count ?? 0 : "—"}
                          </Typography>
                          <Typography sx={{ ...typography.bodySmall, color: (info.error_count ?? 0) > 0 ? ColorsConstants.error : grayFontColor }}>
                            {isTerminal ? info.error_count ?? 0 : "—"}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11.5,
                              fontWeight: 600,
                              color: info.status === "FAILED" ? ColorsConstants.error : info.status === "DOWNLOADED" ? dashboardCardGreenTextColor : grayFontColor,
                            }}
                          >
                            {STATUS_LABELS[info.status] || info.status.toLowerCase()}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                )}

                {anySyncFailed && (
                  <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.error, mt: 1.5 }}>
                    {Object.entries(moduleProgress || {})
                      .filter(([, info]) => info.status === "FAILED")
                      .map(([mod, info]) => `${mod}: ${info.error_message || "unknown error"}`)
                      .join(" · ")}
                  </Typography>
                )}
              </Box>

              <Button
                variant="text"
                onClick={handleDisconnect}
                disabled={disconnecting}
                startIcon={disconnecting ? <CircularProgress size={14} sx={{ color: ColorsConstants.error }} /> : null}
                sx={{ textTransform: "none", ...typography.bodySmall, color: ColorsConstants.error }}
              >
                {disconnecting ? "Disconnecting…" : "Disconnect Zoho CRM"}
              </Button>
            </Box>
          )}

          {/* ── Footer navigation for wizard steps ── */}
          {!checkingStatus && showFooterNav && (
            <Box
              sx={{
                display: "flex", justifyContent: "space-between", mt: 3, pt: 2.5,
                borderTop: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
              }}
            >
              <Button
                onClick={goBack}
                startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
                sx={{ textTransform: "none", color: grayFontColor, ...typography.bodyLarge, fontWeight: 600 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={goForward}
                disabled={(step === "modules" && selectedModules.length === 0) || syncing}
                startIcon={syncing ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
                endIcon={step !== "schedule" ? <ArrowForwardIcon sx={{ fontSize: 15 }} /> : null}
                sx={{
                  textTransform: "none", ...typography.bodyLarge, fontWeight: 700, px: 3,
                  bgcolor: ColorsConstants.secondaryBackgroundColor,
                  "&:hover": { bgcolor: ColorsConstants.primaryBackgroundColor },
                }}
              >
                {step === "schedule" ? (syncing ? "Syncing…" : "Save & start syncing") : "Continue"}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ZohoTab;
